import { translate } from "app/language/service"
import { APP_CONTEXTS, IRootState, NOT_SAVED_ID } from "app/model"
import { getStore } from "app/store/store"
import { getActiveAppContext } from "app/tools/common"
import { campaignRPO } from "campaign/repository"
import { CURRENCIES } from "currency/model"
import { getCadExchangeRateByCurrency } from "currency/tools/common"
import {
  IMapInventoryLegendData,
  INVENTORY_MAP_VIEW_TYPES,
  IRefreshInventoryParams,
  MAP_INVENTORY_QUANTILE_STEPS
} from "form/inventory/model"
import { refreshInventoryMapData } from "form/map/tools/inventory"
import { state as formState } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getForm } from "form/tools/common"
import { messageSVC } from "message/service"
import { getUserCurrencyId } from "user/tools/common"
import { IFormInventory } from "../model"

export function getInventory(state?: IRootState): IFormInventory {
  if (!state) {
    state = getStore().getState()
  }
  const context = state.app.activeContext
  if (context === null) {
    throw Error("No context is active")
  }
  switch (context) {
    case APP_CONTEXTS.CAMPAIGN_CREATE: {
      return state.form.campaignCreateForm.inventory
    }
    case APP_CONTEXTS.CAMPAIGN_EDIT: {
      return state.form.campaignEditForm.inventory
    }
    case APP_CONTEXTS.FORECAST_CREATE: {
      return state.form.forecastCreateForm.inventory
    }
    case APP_CONTEXTS.FORECAST_EDIT: {
      return state.form.forecastEditForm.inventory
    }
    case APP_CONTEXTS.DSP_DEAL_CREATE: {
      return state.form.dspDealCreateForm.inventory
    }
    case APP_CONTEXTS.DSP_DEAL_EDIT: {
      return state.form.dspDealEditForm.inventory
    }
  }
}

export async function refreshInventory(params?: IRefreshInventoryParams): Promise<void> {
  if (!params) {
    params = {}
  }
  const context = getActiveAppContext()
  if (context === null) {
    return Promise.reject("No context is active")
  }
  const form = getForm()
  const exchangeRateByCurrency = getCadExchangeRateByCurrency()
  if (formState.isInventoryRefreshing) {
    formState.campaignIdToRefreshForInventory = params.campaignId
    return
  }
  refreshInventoryMapData()
  try {
    if (typeof params.campaignId !== "number" || params.campaignId === NOT_SAVED_ID) {
      params.campaignId = getInventory().tempCampaignId
    }
    if (params.campaignId === NOT_SAVED_ID) {
      return Promise.resolve()
    }
    getStore().dispatch(formActionCreator.startRefreshingInventory(context))
    const result = await campaignRPO.getCampaignGlobalForecast(
      params.campaignId as number,
      exchangeRateByCurrency[form.configuration.budget.currency_id]
    )
    if (!params.ignoreWarnMessage) {
      const previousInventory = getInventory()
      if (previousInventory.totalScreens > 0 && result.total_screens === 0) {
        messageSVC.warning(translate("form.inventory.down.to.zero"))
      } else if (previousInventory.totalScreens === 0 && result.total_screens > 0) {
        messageSVC.info(translate("form.inventory.up"))
      }
    }
    getStore().dispatch(formActionCreator.refreshInventory(result, context, params.campaignName))
    if (formState.campaignIdToRefreshForInventory !== null) {
      const toRefreshCampaignId = formState.campaignIdToRefreshForInventory
      formState.campaignIdToRefreshForInventory = null
      refreshInventory({ campaignId: toRefreshCampaignId })
    }
  } catch (ex) {
    console.error(ex)
    getStore().dispatch(formActionCreator.hasRefreshInventoryError(context))
  }
}

/**
 * Cost per mile average: cost of all ads divided by number of people who see it (impressions) by 1000
 * => cost / impressions x 1000
 *
 * @returns eCPM
 */
export function getInventoryECPM(inventory: IFormInventory): number {
  const { totalSpend, totalImpressions } = inventory
  return getEcpm(totalSpend, totalImpressions)
}

export function getEcpm(totalSpend: number, totalImpressions: number): number {
  if (!totalSpend || !totalImpressions) {
    return 0
  }
  return Number(((totalSpend / totalImpressions) * 1000).toFixed(2))
}

export function getCurrentFormCurrencyId(): CURRENCIES {
  return getForm().configuration.budget.currency_id || getUserCurrencyId()
}

export function getCurrencySymbol(currencyId?: CURRENCIES | string): string {
  if (!currencyId) {
    currencyId = getUserCurrencyId()
  }
  switch (currencyId) {
    case "usd":
    case "USD": {
      return "US$"
    }
    case "cad":
    case "CAD": {
      return "CA$"
    }
    case "eur":
    case "EUR": {
      return "€"
    }
    default: {
      return currencyId
    }
  }
}

export function getMapViewType(): INVENTORY_MAP_VIEW_TYPES {
  return getInventory().mapViewType
}

export function setMapInventoryLegendData(data: IMapInventoryLegendData): void {
  const context = getActiveAppContext()
  if (!context) {
    return
  }
  getStore().dispatch(formActionCreator.setMapInventoryLegendData(context, data))
}

export function getImpressionsStepBackgroundColors(impressionCountQuantiles: number[]): string[] {
  return [
    "#faeae7",
    "#f4d6d0",
    "#efc2b8",
    "#e9aea1",
    "#e49989",
    "#de8572",
    "#d9715a",
    "#d35d43",
    "#ce482b",
    "#c93515"
  ].slice(MAP_INVENTORY_QUANTILE_STEPS - impressionCountQuantiles.length)
}

export function getImpressionsStepTextColors(impressionCountQuantiles: number[]): string[] {
  return [
    "#c93515",
    "#c93515",
    "#c93515",
    "#c93515",
    "#c93515",
    "#faeae7",
    "#faeae7",
    "#faeae7",
    "#faeae7",
    "#faeae7"
  ].slice(MAP_INVENTORY_QUANTILE_STEPS - impressionCountQuantiles.length)
}
